/**
 * Offline-first query behaviour for the native shells (P5).
 *
 * Three things, all Capacitor-only (web/desktop keep their defaults):
 *
 *   1. ``staleTime`` — cached query data is considered fresh for a
 *      while, so navigating back to a page paints from the in-memory
 *      cache instead of re-awaiting the network. Combined with the
 *      fetch wrapper's mirror-first reads (offline-fetch.ts) this is
 *      what makes pages open instantly.
 *
 *   2. Mirror → UI plumbing — when the fetch wrapper's background
 *      revalidation refreshes the on-device mirror it dispatches
 *      ``afri:mirror-updated``; we invalidate every active query so
 *      React re-renders with the fresh rows. Debounced: a page load
 *      may revalidate a dozen URLs in a burst.
 *
 *   3. Reconnect refresh — when the radio comes back (native network
 *      event) or the outbox confirms the cloud is reachable, refetch
 *      what's on screen so a field annotator sees the latest without
 *      pulling to refresh.
 *
 * Persistence of the query cache across app restarts is intentionally
 * NOT done here: the IndexedDB mirror already survives restarts and
 * the wrapper answers from it on the first render, which gives the
 * same cold-start experience without duplicating storage.
 */
import type { QueryClient } from "@tanstack/react-query";
import { isCapacitor } from "./host";

const STALE_MS = 60_000;

export const installOfflineFirstQueries = (client: QueryClient): void => {
  if (typeof window === "undefined" || !isCapacitor()) return;
  const w = window as { __afri_ofq_installed?: boolean };
  if (w.__afri_ofq_installed) return;
  w.__afri_ofq_installed = true;

  client.setDefaultOptions({
    queries: {
      ...(client.getDefaultOptions().queries ?? {}),
      staleTime: STALE_MS,
      // Retrying a failed read is pointless when the wrapper already
      // fell back to the mirror or queued the write; retries just
      // spend battery on a dead radio.
      retry: 1,
      refetchOnReconnect: true,
    },
  });

  let timer: number | null = null;
  const invalidateSoon = () => {
    if (timer != null) return;
    timer = window.setTimeout(() => {
      timer = null;
      client.invalidateQueries({ type: "active" }).catch(() => {});
    }, 300);
  };
  window.addEventListener("afri:mirror-updated", invalidateSoon);
  // A successful outbox drain (offline writes reached the cloud) is
  // infrequent but changes server state broadly — annotations counted,
  // task status flipped, dashboard progress moved. Invalidate ALL
  // queries, not just active ones, so a Data Manager or dashboard the
  // user opens AFTER the drain refetches instead of serving stale
  // cached counts. Debounced via the same timer.
  let drainTimer: number | null = null;
  window.addEventListener("afri:outbox-drained", () => {
    if (drainTimer != null) return;
    drainTimer = window.setTimeout(() => {
      drainTimer = null;
      client.invalidateQueries().catch(() => {});
    }, 300);
  });
  // ``afri:online-confirmed`` is a 12s HEARTBEAT while online, not a
  // transition. Invalidating on every beat refetched every page's
  // queries every 12s (visible as constant spinners). React only to
  // the offline→online edge.
  let wasOnline = true;
  window.addEventListener("afri:online-confirmed", () => {
    if (!wasOnline) invalidateSoon();
    wasOnline = true;
  });
  window.addEventListener("afri:cloud-down", () => {
    wasOnline = false;
  });
  let nativeWasConnected = true;
  window.addEventListener("afri:native-network", (e: Event) => {
    const detail = (e as CustomEvent<{ connected?: boolean }>).detail;
    const now = !!detail?.connected;
    if (now && !nativeWasConnected) invalidateSoon();
    nativeWasConnected = now;
  });
};
